// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/service/actionCreator";
import type {
  DataSecurityGeneralSettings,
  RegistrationEmails,
} from "@modules/service/reducer/system-access/types";
import {
  UpdateDataSecurityGeneralSettings,
  GetDataSecurityGeneralSettings,
  GetClientLogo,
  UploadClientLogo,
  SendBulkRegistrationEmails,
  GetBulkRegistrationEmails,
  GetBulkRegistrationEmailsTargets,
  GetPlatformDataSecurity,
  UpdateRegistrationEmails,
  ExportBulkRegistrationEmails,
  GetEmployeeRegistrationEmailsTargets,
  SendEmployeeRegistrationEmails,
  GetPasswordPolicy,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { dataURLtoFile } from "@utils/file";
import { downloadCsvFile } from "@sagas/utils";
import { removeNullAndUndefinedProps } from "@transformers/object";

type SystemAccessRequest = $Exact<{
  clientId: string,
}>;

type SystemAccessUpdateRequest = $Exact<{
  ...SystemAccessRequest,
  dataSecurityGeneralSettings: DataSecurityGeneralSettings,
}>;

type RegistrationEmailsUpdateRequest = $Exact<{
  ...SystemAccessRequest,
  formData: RegistrationEmails,
}>;

export function* onGetDataSecurityGeneralSettings({
  payload,
}: Action<SystemAccessRequest>): Generator<*, void, { data: DataSecurityGeneralSettings }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-general-settings`,
    );

    yield put(
      actionCreators.GetDataSecurityGeneralSettingsSuccess(
        removeNullAndUndefinedProps(response.data),
      ),
    );
  } catch (e) {
    yield put(actionCreators.GetDataSecurityGeneralSettingsError(e.response));
  }
}
export function* onGetPasswordPolicy({
  payload,
}: Action<SystemAccessRequest>): Generator<*, void, { data: DataSecurityGeneralSettings }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.keycloakServerUrl}/realms/goodshape-central/gs-password-policy/effective/${payload.clientId}`,
    );

    yield put(actionCreators.GetPasswordPolicySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetPasswordPolicyError(e.response));
  }
}

export function* onUpdateDataSecurityGeneralSettings({
  payload,
}: Action<SystemAccessUpdateRequest>): Generator<*, void, { data: DataSecurityGeneralSettings }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-general-settings`,
      payload.dataSecurityGeneralSettings,
    );

    yield all([
      put(actionCreators.UpdateDataSecurityGeneralSettingsSuccess()),
      put(actionCreators.ToggleSystemAccessPasswordResetModal(false)),
      put(actionCreators.ToggleSystemAccessLogOutLockModal(false)),
      put(actionCreators.ToggleAddIpRestrictionModal(false)),
      put(actionCreators.GetDataSecurityGeneralSettingsSuccess(response.data)),
      put(sendSuccessMessage("SYSTEM_SETTINGS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDataSecurityGeneralSettingsError(e.response));
  }
}

export function* onGetClientLogo({ payload }: Action<SystemAccessRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: logo } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/logo`,
    );
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/logo/file/${logo.id}`,
      {
        responseType: "blob",
        headers: {
          Accept: "application/json",
        },
      },
    );

    const logoUrl = URL.createObjectURL(response.data);

    yield put(actionCreators.GetClientLogoSuccess(logoUrl));
  } catch (e) {
    yield put(actionCreators.GetClientLogoError());
  }
}

type UpdateClientLogoRequest = $Exact<{
  ...SystemAccessRequest,
  formData: Object,
}>;

export function* onUploadClientLogo({
  payload,
}: Action<UpdateClientLogoRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.formData) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const file = dataURLtoFile(payload.formData.file);
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/logo`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.UploadClientLogoSuccess(response.data)),
      put(
        actionCreators.GetClientLogo({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("UPLOAD_LOGO_SUCCESS")),
    ]);
  } catch (e) {
    yield put(actionCreators.UploadClientLogoError(e.response));
  }
}

type SendBulkRegistrationEmailsRequest = $Exact<{
  clientId: ?number,
}>;

export function* onSendBulkRegistrationEmails({
  payload,
}: Action<SendBulkRegistrationEmailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/bulk-registration/send-email`,
    );

    yield all([
      put(actionCreators.SendBulkRegistrationEmailsSuccess()),
      put(actionCreators.ToggleRegistrationEmailsModal(false)),
      put(sendSuccessMessage("BULK_REGISTRATION_EMAILS_SENT")),
      put(
        actionCreators.GetBulkRegistrationEmailsTargets({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.SendBulkRegistrationEmailsError(e.response));
  }
}

type GetBulkRegistrationEmailsRequest = $Exact<{
  clientId: number,
  page: string,
}>;

export function* onGetBulkRegistrationEmails({
  payload,
}: Action<GetBulkRegistrationEmailsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.page) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const sort = "last_name";
    const { clientId, page } = payload;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/bulk-registration/list?limit=${config.results15PerPage}&page=${page}&sort=${sort}`,
    );

    yield put(actionCreators.GetBulkRegistrationEmailsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetBulkRegistrationEmailsError(e.response));
  }
}

type GetBulkRegistrationEmailsTargetRequest = $Exact<{
  clientId: number,
}>;

export function* onGetBulkRegistrationEmailsTargets({
  payload,
}: Action<GetBulkRegistrationEmailsTargetRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/bulk-registration`,
    );

    yield put(
      actionCreators.GetBulkRegistrationEmailsTargetsSuccess(
        response.data.bulkRegistrationEmailTargets,
      ),
    );
  } catch (e) {
    yield put(actionCreators.GetBulkRegistrationEmailsTargetsError(e.response));
  }
}

export function* onGetEmployeeRegistrationEmailsTargets({
  payload,
}: Action<SystemAccessRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/service-admin/client/${payload.clientId}/service-config/mobile-app/bulk-registration/count`,
    );

    yield put(
      actionCreators.GetEmployeeRegistrationEmailsTargetsSuccess(
        response.data.bulkRegistrationEmailTargets,
      ),
    );
  } catch (e) {
    yield put(actionCreators.GetEmployeeRegistrationEmailsTargetsError(e.response));
  }
}

export function* onSendEmployeeRegistrationEmails({
  payload,
}: Action<SystemAccessRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.post,
      `api/v1/service-admin/client/${payload.clientId}/service-config/mobile-app/bulk-registration/send-email`,
    );

    yield all([
      put(actionCreators.SendEmployeeRegistrationEmailsSuccess()),
      put(actionCreators.ToggleEmployeeRegistrationEmailsModal(false)),
      put(sendSuccessMessage("EMPLOYEE_EMAILS_SENT")),
      put(
        actionCreators.GetEmployeeRegistrationEmailsTargets({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.SendEmployeeRegistrationEmailsError(e.response));
  }
}

export function* onGetPlatformDataSecurity({
  payload,
}: Action<SystemAccessRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-myfirstcare`,
    );

    yield put(actionCreators.GetPlatformDataSecuritySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetPlatformDataSecurityError(e.response));
  }
}

export function* onUpdateRegistrationEmails({
  payload,
}: Action<RegistrationEmailsUpdateRequest>): Generator<
  *,
  void,
  { data: DataSecurityGeneralSettings },
> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-myfirstcare`,
      payload.formData,
    );

    yield all([
      put(actionCreators.UpdateRegistrationEmailsSuccess()),
      put(actionCreators.ToggleStandardRegistrationEmailsModal(false)),
      put(actionCreators.GetPlatformDataSecuritySuccess(response.data)),
      put(sendSuccessMessage("REGISTRATION_EMAILS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateRegistrationEmailsError(e.response));
  }
}

type GetExportBulkRegistrationEmailsRequest = $Exact<{
  clientId: number,
}>;

export function* onExportBulkRegistrationEmails({
  payload,
}: Action<GetExportBulkRegistrationEmailsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { clientId } = payload;

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/bulk-registration/export`,
      {
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportBulkRegistrationEmailsSuccess(response.data));
    yield downloadCsvFile(response.data, "bulk-registrations-emails.csv");
  } catch (e) {
    yield put(actionCreators.ExportBulkRegistrationEmailsError(e.response));
  }
}

export function* SystemAccessSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDataSecurityGeneralSettings, onGetDataSecurityGeneralSettings),
    takeEvery(UpdateDataSecurityGeneralSettings, onUpdateDataSecurityGeneralSettings),
    takeEvery(GetClientLogo, onGetClientLogo),
    takeEvery(UploadClientLogo, onUploadClientLogo),
    takeEvery(SendBulkRegistrationEmails, onSendBulkRegistrationEmails),
    takeEvery(GetBulkRegistrationEmails, onGetBulkRegistrationEmails),
    takeEvery(GetBulkRegistrationEmailsTargets, onGetBulkRegistrationEmailsTargets),
    takeEvery(GetPlatformDataSecurity, onGetPlatformDataSecurity),
    takeEvery(UpdateRegistrationEmails, onUpdateRegistrationEmails),
    takeEvery(ExportBulkRegistrationEmails, onExportBulkRegistrationEmails),
    takeEvery(GetEmployeeRegistrationEmailsTargets, onGetEmployeeRegistrationEmailsTargets),
    takeEvery(SendEmployeeRegistrationEmails, onSendEmployeeRegistrationEmails),
    takeEvery(GetPasswordPolicy, onGetPasswordPolicy),
  ]);
}

export default SystemAccessSagas;
